import dragSpineStyles from "../cards/card-drag-spine.module.css"
import styles from "./card-google-classroom.module.css"

import IconGoogleLogo from "@shared/components/icons/GoogleLogo"
import IconLink from "@shared/components/icons/Link"
import IconPaperclip from "@shared/components/icons/Paperclip"
import { useNotifier } from "@shared/components/providers/notifier-context"
import { useDocument } from "@shared/components/providers/SupergrainProvider"
import { cx } from "@shared/components/untitledui/util/cx"
import type { CardAttachment } from "@shared/models/card-stack"
import type { GoogleClassPostStatus } from "@shared/models/google-classroom"
import { CardPublicNotice } from "@shared/unified-tiptap/editor-ui/card-public-notice/CardPublicNotice"
import CardComments from "@shared/unified-tiptap/editor-ui/comments/CardComments"
import { DraggableNodeViewWrapper } from "@shared/unified-tiptap/editor-ui/drag-handle/DraggableNodeViewWrapper"
import {
  CARD_BOTTOM_SEPARATOR_CLASS,
  CARD_COMMENTS_BOX_CLASS,
  CARD_WRAPPER_CLASS,
  cardWrapperData,
  getCardId,
  useCardComments,
  useIsLastCard,
} from "@shared/unified-tiptap/util/card"
import { SECTION_COLOR_LIGHT_MAP, SECTION_COLOR_MAP } from "@shared/util/colors"
import { tracked } from "@supergrain/kernel/react"
import type { ExtensionCardGoogleClassroomAttrs } from "@tiptap-content/extensions/card-google-classroom/card-google-classroom-content-extension"
import type { Link } from "@tiptap-content/extensions/link/link-attrs"
import type { Node as PMNode } from "@tiptap/pm/model"
import { type ReactNodeViewProps } from "@tiptap/react"
import { reportMenuOpenChange } from "../../analytics/open-menu-event"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { asAttachmentId, asCardId } from "../../analytics/types"
import { CardButtons } from "../../editor-ui/card-buttons/CardButtons"
import { useUiContext } from "../../unified-tiptap-ui-context"
import { useHoverChrome } from "../../use-hover-chrome"
import { cardAttachmentFromLink } from "./card-google-classroom-extension"

// In its own file (with only component exports) so that edits hot-reload
// via React Fast Refresh instead of reloading the page.

/**
 * The Google Classroom card's node view. Copy-and-modified from CardExtensionComponent +
 * CardTitleComponent, but the title, contents, and attachments are rendered directly as
 * React content (from the node's attrs) instead of as ProseMirror descendants: the card
 * is mostly non-interactive in the editor — clicking it opens the Google Classroom
 * sidebar, where the actual editing happens.
 */
// tracked(): useHoverChrome reads this surface's verdict as a signal.
export const CardGoogleClassroomComponent = tracked((props: ReactNodeViewProps) => {
  const attrs = props.node.attrs as ExtensionCardGoogleClassroomAttrs

  // The card is the reveal region for its own chrome — the fixed title buttons —
  // just like a normal card (the same "cardButtons" surface).
  const { chromeVisible, chromeProps } = useHoverChrome("cardButtons")
  const isLastCard = useIsLastCard(props.editor, attrs.id)

  const { openGoogleClassroomSidebar: openSidebar } = useUiContext()
  const comments = useCardComments(attrs)
  const hasComments = comments.length > 0
  const notify = useNotifier()

  // Fetch the card's mediator (posting info from the backend).
  const mediatorHandle = useDocument("google-classroom-card-mediator", attrs.id)
  const mediator = mediatorHandle.status === "success" ? mediatorHandle.value.attributes : undefined
  const isPublished = (s: GoogleClassPostStatus) => s.status === "PUBLISHED"
  const isScheduled = (s: GoogleClassPostStatus) => s.status === "SCHEDULED"
  const postedCount = mediator?.googleClassStatuses.filter(isPublished).length ?? 0
  const scheduledCount = mediator?.googleClassStatuses.filter(isScheduled).length ?? 0

  // The card has edits that haven't been sent to Google Classroom yet. -1 is the sentinel
  // for "never published", so it never counts as stale (matches the legacy guard).
  const hasUnpublishedChanges =
    mediator !== undefined &&
    attrs.revision > 0 &&
    attrs.revision > mediator.cardRevisionAtLastUpdate &&
    mediator.cardRevisionAtLastUpdate !== -1

  const handleClick = (event: React.MouseEvent) => {
    if (!openSidebar) return

    // Let attachment links behave as links, and don't treat the card buttons (Add /
    // Menu, which live inside this card box) as card clicks.
    if ((event.target as Element).closest("a, button")) return
    openSidebar(attrs.id)
    reportMenuOpenChange(props.editor, "google-classroom-sidebar", true, () => asCardId(attrs.id))
  }

  /**
   * Appends an attachment to the card's `attachments` attr, converting the picked Link
   * to the legacy CardAttachment shape it stores, and bumps `revision` so the card reads
   * as having changes not yet sent to Google Classroom.
   *
   * Google Classroom rejects edits to an assignment that has already posted, so we first
   * wait for the mediator (posting status) to load and refuse the change with a toast if
   * any class has published this card.
   */
  const addAttachment = async (link: Link) => {
    const pos = props.getPos()
    if (pos === undefined) return

    // Make sure the mediator has loaded before we decide. It rejects when the card was
    // never posted (no mediator document), in which case nothing is published.
    try {
      await mediatorHandle.promise
    } catch {
      // No mediator — the card has never posted, so attachments are still editable.
    }
    const statuses =
      mediatorHandle.status === "success" ? mediatorHandle.value.attributes.googleClassStatuses : []
    if (statuses.some(isPublished)) {
      notify("warning", "Attachments can’t be modified once assignments have posted.")
      return
    }

    const node = props.editor.state.doc.nodeAt(pos)
    if (!node) return
    const latestAttrs = node.attrs as ExtensionCardGoogleClassroomAttrs

    const attachment = cardAttachmentFromLink(link)
    props.editor
      .chain()
      .label(OPERATION_CONTRACTS.OP_INSERT_GOOGLE_CLASSROOM_ATTACHMENT, "add-button", {
        cardId: asCardId(attrs.id),
        attachmentIds: [asAttachmentId(attachment.id)],
        source: link.source,
      })
      .command(({ tr }) => {
        tr.setNodeAttribute(pos, "attachments", [...latestAttrs.attachments, attachment])
        tr.setNodeAttribute(pos, "revision", latestAttrs.revision + 1)
        return true
      })
      .run()
  }

  /**
   * Takes on the copied card's content while keeping this card's identity: `id` (the
   * backend's Google Classroom post is keyed to it) stays, and `revision` bumps so the
   * card reads as having changes not yet sent to Google Classroom.
   */
  const pasteCard = (copiedCard: PMNode) => {
    const cardPos = props.getPos()
    if (cardPos === undefined) return

    const currentAttrs = props.node.attrs as ExtensionCardGoogleClassroomAttrs
    const newAttrs: ExtensionCardGoogleClassroomAttrs = {
      ...(copiedCard.attrs as ExtensionCardGoogleClassroomAttrs),
      id: currentAttrs.id,
      revision: currentAttrs.revision + 1,
    }
    const sourceCardId = getCardId(copiedCard)
    const destinationCardId = getCardId(props.node)
    const chain = props.editor.chain()
    if (sourceCardId && destinationCardId) {
      chain.label(OPERATION_CONTRACTS.OP_PASTE_CARD, "card-menu", {
        sourceCardId,
        destinationCardId,
      })
    }
    chain
      .command(({ tr }) => {
        tr.setNodeMarkup(cardPos, undefined, newAttrs)
        return true
      })
      .run()
  }

  return (
    <DraggableNodeViewWrapper
      {...props}
      // -mr-9/pr-9 extend the hover zone right, past the card edge where the fixed
      // buttons hang; pt-2 owns the 8px of white above the card so it stays INSIDE the
      // reveal region (a top margin on the card box would collapse out — the same dead
      // strip fixed for normal cards). styles.cardWrapper stays as the hook for the
      // drag-handle reveal.
      wrapperClassName={cx(
        CARD_WRAPPER_CLASS,
        props.editor.isEditable && dragSpineStyles.cardDragSpineWrapper,
        styles.cardWrapper,
      )}
      handleClassName={dragSpineStyles.cardDragSpine}
      wrapperProps={{ ...chromeProps, ...cardWrapperData({ chromeVisible, isLastCard }) }}
    >
      {/* Full-bleed card box like a normal card (-mx-3 cancels the lesson body's p-3). No
          vertical margin — the 8px above lives on the wrapper (pt-2), the 8px below is the
          next card's wrapper pt-2. unified-tiptap-card is a global hook other extensions
          select on; styles.card stays for the native-selection suppression + the
          drag-handle reveal. */}
      <div
        className={cx(
          "unified-tiptap-card relative -mx-3 cursor-pointer px-3 select-none",
          styles.card,
        )}
        data-id={attrs.id}
        contentEditable={false}
      >
        <div
          onClick={handleClick}
          className="gc-bg-card rounded-md p-1"
          style={{ backgroundColor: SECTION_COLOR_LIGHT_MAP[attrs.color] }}
        >
          <CardButtons
            {...props}
            mode="cardGoogleClassroom"
            // The node is an atom, so there is no inside: the Add menu always acts on
            // the card node's own position (the widget-style target).
            getAddTargetPos={() => props.getPos() ?? null}
            pasteCard={pasteCard}
            addAttachment={addAttachment}
            // Hang just off the card's right edge.
            className={cx("top-0 right-[-14px]", styles.cardButtons)}
          />
          {hasUnpublishedChanges && (
            <div className="mb-3 rounded-md border border-card-panel bg-white p-[9px] shadow-lg">
              <div className="mb-1.5 border-b border-card-hairline pb-[3px] text-center text-[0.84375rem] leading-none font-bold">
                Changes not sent to Google Classroom!
              </div>
              <div className="leading-none font-normal text-card-muted">
                Click this card and then click "Update" to make sure these changes show up on Google
                Classroom.
              </div>
            </div>
          )}
          <div className="mb-1.5 flex flex-row items-center justify-center gap-[3px] border-b border-card-banner py-1.5 text-card-ink [&_svg]:size-3 [&_svg]:shrink-0">
            <IconGoogleLogo />
            <div className="text-[12px] leading-[0.8] font-bold uppercase">Google Classroom</div>
          </div>
          {attrs.postType !== "ANNOUNCEMENT" && (
            <h2
              className="min-h-[1.125rem] text-[1.125rem] leading-tight font-bold break-words"
              style={{ color: SECTION_COLOR_MAP[attrs.color] }}
            >
              {attrs.title}
            </h2>
          )}
          <div className="min-h-[1lh] break-words whitespace-pre-wrap">{attrs.value}</div>
          {attrs.attachments.map((attachment) => (
            <AttachmentLine key={attachment.id} attachment={attachment} />
          ))}
          {(postedCount > 0 || scheduledCount > 0) && (
            <div className="mt-1.5 flex flex-row border-t border-card-hairline pt-1.5">
              <span className="mr-[3px] font-semibold">Classes</span>
              {postedCount} Posted. {scheduledCount} Scheduled.
            </div>
          )}
        </div>
      </div>
      {hasComments && (
        // In addition to contentEditable={false}, use tabIndex={-1} to allow
        // non-ProseMirror-controlled selections inside the comments.
        // From https://discuss.prosemirror.net/t/allow-text-selection-in-leaf-nodeviews/3438/2
        <div className={CARD_COMMENTS_BOX_CLASS} contentEditable={false} tabIndex={-1}>
          <CardComments comments={comments} />
        </div>
      )}
      {attrs.isPublic && <CardPublicNotice />}
      <div className={CARD_BOTTOM_SEPARATOR_CLASS} contentEditable={false} />
    </DraggableNodeViewWrapper>
  )
})

/** One attachment, shown like a linkBlock's attachment line (see LinkComponent). */
const AttachmentLine = ({ attachment }: { attachment: CardAttachment }) => {
  return (
    <a
      href={attachment.url}
      title={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-1.5 pt-[3px] text-inherit no-underline"
    >
      {attachment.type === "link" ? (
        <IconLink className="mt-0.5 size-[15px] shrink-0 text-card-ink" />
      ) : (
        <IconPaperclip className="mt-0.5 size-[15px] shrink-0 text-card-ink" />
      )}
      <span className="min-w-0 break-words [word-break:break-word] text-card-link underline">
        {attachment.title || attachment.url}
      </span>
    </a>
  )
}
